import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { fetchWithAuth } from "../../api/fetchWithAuth";
import OrgLicenseSummary from "../../components/OrgLicenseSummary";
import OrgUsersTable from "../../components/OrgUsersTable";
import OrgInviteForm from "./OrgInviteForm";
import OrgPendingInvites from "./OrgPendingInvites";
import OrgImageryEntitlements from "../../components/OrgImageryEntitlements";
import OrgAnalyticsEntitlements from "../../components/OrgAnalyticsEntitlements";
import { usePlatformOrgContext } from "../../stores/usePlatformOrgContext";
import { useOrgStore } from "../../stores/useOrgStore";

// REM-63. A refused response has a body, but not the body the caller expects,
// so `data.users || []` turned a 403 into an empty table and `return` turned
// one into an empty invite list. Both read as "your organization has nobody
// in it" — an answer, not a failure. That is how the REM-62 capability defect
// stayed invisible in the UI while the server refused every request.
//
// The server speaks in several shapes here: `{error, detail, code}` from the
// middleware, and `{error}` alone from the route handlers. Prefer detail,
// fall back to error, then to the status code — never to silence.
async function describeFailure(res, fallback) {
  let msg = null;
  try {
    const body = await res.json();
    msg = body?.detail || body?.error || null;
  } catch { /* not JSON — the status is all we have */ }
  if (res.status === 403) {
    return msg
      ? `Not permitted: ${msg}`
      : "Not permitted to administer this organization.";
  }
  return msg ? `${fallback}: ${msg}` : `${fallback} (HTTP ${res.status})`;
}

export default function OrgAdminPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [license, setLicense] = useState(null);

  // Invites live here rather than in the list component: the invite form and
  // the list are now separate siblings (the form is pinned, the list scrolls),
  // and a create/resend/revoke has to refresh invites + users + license in
  // lock-step. Same reason `users` and `license` live here.
  const [invites, setInvites] = useState([]);

  // Separate from `error` on purpose: a refused invite list must not blank the
  // whole page the way a refused user list does — the users table is still
  // valid. See REM-63.
  const [invitesError, setInvitesError] = useState(null);

  // Row-level failure from updateUser, shaped like OrgPendingInvites' rowError
  // so both tables report the same way. Replaces a pair of alert() calls — the
  // only blocking dialogs left in this area.
  const [userError, setUserError] = useState(null); // { id, message }

  const org = useOrgStore((s) => s.activeOrg);
  const orgRole = useOrgStore((s) => s.role);

  const TABS = ["users", "imagery", "analytics"];
  const [activeTab, setActiveTab] = useState("users");

  const { activeOrgId } = usePlatformOrgContext.getState();

  async function loadLicense() {
    try {
      const res = await fetchWithAuth("/api/v1/org/license");
      const data = await res.json();
      setLicense(data);
    } catch (e) {
      console.error(e);
      // Non-fatal — invites panel will fall back to enabled if license is null.
    }
  }

  async function loadUsers() {
    try {
      const res = await fetchWithAuth("/api/v1/org/users");
      if (!res.ok) {
        setError(await describeFailure(res, "Failed to load users"));
        return;
      }
      const data = await res.json();
      setUsers(data.users || []);
      setError(null);
    } catch (e) {
      console.error(e);
      setError("Failed to load users — network error");
    }
  }

  async function loadInvites() {
    try {
      const res = await fetchWithAuth("/api/v1/admin/orgs/invites");
      if (!res.ok) {
        console.error("[invites] list failed", res.status);
        // An empty list and a refused list look identical on screen. Say which.
        setInvites([]);
        setInvitesError(await describeFailure(res, "Failed to load invites"));
        return;
      }
      setInvites(await res.json());
      setInvitesError(null);
    } catch (e) {
      console.error("[invites] list error", e);
      setInvites([]);
      setInvitesError("Failed to load invites — network error");
    }
  }

  // Initial load: fan out the three independent fetches in parallel and
  // only flip `loading` off once they've all settled. Sequential awaits
  // here would noticeably delay first paint of the page.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.allSettled([loadUsers(), loadLicense()])
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Invites refresh when admins switch orgs in platform mode. Kept off the
  // initial-load effect above so it isn't gated behind the `loading` spinner —
  // it has no bearing on whether the page can paint.
  useEffect(() => {
    loadInvites();
  }, [activeOrgId]);

  async function updateUser(userId, patch) {
    const prev = [...users];
    setUserError(null);
    setUsers(users =>
      users.map(u =>
        u.user_id === userId ? { ...u, ...patch } : u
      )
    );

    try {
      const res = await fetchWithAuth(`/api/v1/org/users/${userId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });

      if (!res.ok) {
        let msg = `Update failed (${res.status})`;
        try {
          const body = await res.json();
          if (body?.error) msg = body.error;
        } catch { /* not JSON */ }
        setUsers(prev);
        setUserError({ id: userId, message: msg });
        return;
      }

      // Server confirmed the change — refresh users AND license in lock-step.
      await Promise.all([loadUsers(), loadLicense()]);
    } catch (e) {
      console.error(e);
      setUsers(prev);
      setUserError({ id: userId, message: "Update failed — network error" });
    }
  }

  // Passed to both invite components so seat counts refresh after a successful
  // create/resend/revoke — otherwise the "Invite" button stays incorrectly
  // enabled (or disabled) until the user reloads the page.
  async function handleInviteChange() {
    await Promise.all([loadUsers(), loadLicense(), loadInvites()]);
  }

  if (loading) {
    return (
      <div className="flex justify-center pt-10">
        <Loader2 className="animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return <div className="p-6 text-red-400">{error}</div>;
  }

  if (orgRole !== "org_admin") {
    return (
      <div className="p-6 text-sm text-gray-400">
        You don't have permission to manage this organization.
      </div>
    );
  }

  // The route renders into <main className="flex-1 overflow-hidden"> (App.jsx),
  // which clips at a definite height. Without h-full + an explicit scroller
  // here, anything past the fold is simply cut off with no scrollbar.
  return (
    <div className="flex h-full flex-col text-sm text-gray-200">

      {/* Header + tabs stay put so the tab strip is always reachable. */}
      <div className="shrink-0 px-6 pt-6 space-y-6">

        {/* Header */}
        <div className="flex flex-col gap-1">
          {org && (
            <span className="inline-flex w-fit items-center text-lg uppercase px-2 py-0.5 rounded bg-bg-soft border border-bg-border text-gray-300">
              {org.name}
            </span>
          )}
          <h1 className="text-lg font-semibold">Organization Admin</h1>
          <p className="text-xs text-gray-400">Manage users and organization access</p>
        </div>

        <div className="flex gap-4 border-b border-bg-border">
          {TABS.map(t => (
            <button
              key={t}
              onClick={() => setActiveTab(t)}
              className={`px-3 py-2 text-xs uppercase tracking-wide border-b-2
        ${activeTab === t
                  ? "border-accent-action text-accent-action"
                  : "border-transparent text-gray-400 hover:text-gray-200"
                }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* The one scroller on the page — shared by every tab.
          Deliberately carries no padding or space-y of its own: a sticky box's
          constraint rect is inset by its OWN margins, so a space-y margin-top
          on the invite bar would pin it that far below the scrollport top and
          leave a band for content to scroll through. Each block below owns its
          spacing instead, which keeps the sticky bar's margin at zero. */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === "users" && (
          <>
            <div className="px-6 pt-6 pb-6">
              <OrgLicenseSummary data={license} />
            </div>

            {/* Pinned so the invite controls stay reachable while the user and
                invite lists scroll under them. Spans the full scroller width so
                the backdrop covers edge to edge — nothing shows through. */}
            <div className="sticky top-0 z-10 px-6 py-4 bg-bg border-b border-bg-border">
              {/* license + invites feed the seat preview: the form mirrors the
                  server's claimed-seat math so a full tier is visible before
                  the admin fills the form out. */}
              <OrgInviteForm
                license={license}
                invites={invites}
                onInvited={handleInviteChange}
              />
            </div>

            <div className="px-6 py-6 space-y-6">
              {/* Users Table */}
              <OrgUsersTable
                users={users}
                onUpdateUser={updateUser}
                rowError={userError}
              />

              {invitesError && (
                <div className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
                  {invitesError}
                </div>
              )}

              <OrgPendingInvites
                invites={invites}
                onChanged={handleInviteChange}
              />
            </div>
          </>
        )}
        {activeTab === "imagery" && (
          <div className="px-6 py-6"><OrgImageryEntitlements /></div>
        )}
        {activeTab === "analytics" && (
          <div className="px-6 py-6"><OrgAnalyticsEntitlements /></div>
        )}
      </div>
    </div>
  );
}
